import { GeoJSONFeature, MapMouseEvent, MapMouseEventType } from 'mapbox-gl';
import { makeAutoObservable } from 'mobx';
import BaseMap from '../interfaces/BaseMap';
import IViewState from '../interfaces/IViewState';
import IVisibleLayers from '../interfaces/IVisibleLayers';
import IZoningFilter from '../interfaces/IZoningFilter';
import LocalStorageService from '../services/localStorageService';
import ZoningFiltersApiService from '../services/zoningFiltersApiService';

export default class MapStore {
    baseMap: BaseMap;
    visibleLayers: IVisibleLayers;
    viewState: IViewState;
    zoningFilters: IZoningFilter[] = [];
    isStyleLoaded: boolean = false;
    mapMouseEventCallbackItems: IMapMouseEventCallbackItem[] = [];
    loadedLogoCompanyIds: number[] = [];

    constructor(
        private localStorageService: LocalStorageService,
        private zoningFiltersApiService: ZoningFiltersApiService,
    ) {
        makeAutoObservable(this);

        this.baseMap = getDefaultBaseMap();
        this.visibleLayers = getDefaultVisibleLayers();
        this.viewState = getDefaultViewState();
    }

    setBaseMap(value: BaseMap) {
        this.baseMap = value;
        this.localStorageService.setBaseMap(value);
    }

    setVisibleLayers(value: IVisibleLayers) {
        this.visibleLayers = value;
        this.localStorageService.setVisibleLayers(value);
    }

    setViewState(value: IViewState) {
        this.viewState = value;
        this.localStorageService.setViewState(value);
    }

    setIsStyleLoaded() {
        this.isStyleLoaded = true;
    }

    setLoadedLogoCompanyIds(ids: number[]) {
        this.loadedLogoCompanyIds = ids;
    }

    async initialize() {
        this.baseMap = this.localStorageService.getBaseMap() || getDefaultBaseMap();
        this.visibleLayers = this.migrateVisibleLayers(this.localStorageService.getVisibleLayers());
        this.viewState = this.localStorageService.getViewState() || getDefaultViewState();
        this.zoningFilters = await this.zoningFiltersApiService.getZoningFilters();
    }

    onMouseMove(key: string, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        return this.addMapMouseEventCallbackItem(key, "mousemove", callback);
    }

    onMouseLeave(key: string, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        return this.addMapMouseEventCallbackItem(key, "mouseleave", callback);
    }

    onMouseDown(key: string, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        return this.addMapMouseEventCallbackItem(key, "mousedown", callback);
    }

    onMouseUp(key: string, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        return this.addMapMouseEventCallbackItem(key, "mouseup", callback);
    }

    onMouseClick(key: string, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        return this.addMapMouseEventCallbackItem(key, "click", callback);
    }

    onMouseDoubleClick(key: string, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        return this.addMapMouseEventCallbackItem(key, "dblclick", callback);
    }

    addMapMouseEventCallbackItem(key: string, type: MapMouseEventType, callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void): () => void {
        this.mapMouseEventCallbackItems = [
            ...this.mapMouseEventCallbackItems,
            {
                key: key,
                type: type,
                callback: callback,
            },
        ];

        return () => this.mapMouseEventCallbackItems = this.mapMouseEventCallbackItems.filter(item => item.key !== key || item.type !== type);
    }

    callMapMouseEventCallbacks(e: MapMouseEvent) {
        this.mapMouseEventCallbackItems.filter(item => item.type === e.type).forEach(item => item.callback(e.features && e.features[0], e));
    }

    setAllLayersVisible() {
        this.setVisibleLayers({
            openDeals: true,
            wonDeals: true,
            lostDeals: true,
            leads: true,
            leaseComps: true,
            saleComps: true,
            favorites: true,
            zoning: true,
            landUse: true,
            iosZoning: true,
            parcels: true,
            counties: true,
            aadt: true,
            opportunityZones: true,
            rails: true,
            pointsOfInterest: true,
        });
    }

    setDealsAndIOSZoningLayersVisible() {
        this.setVisibleLayers({
            openDeals: true,
            wonDeals: true,
            lostDeals: true,
            leads: false,
            leaseComps: false,
            saleComps: false,
            favorites: false,
            zoning: false,
            landUse: false,
            iosZoning: true,
            parcels: false,
            counties: false,
            aadt: false,
            opportunityZones: false,
            rails: false,
            pointsOfInterest: false,
        });
    }

    setNoneLayersVisible() {
        this.setVisibleLayers({
            openDeals: false,
            wonDeals: false,
            lostDeals: false,
            leads: false,
            leaseComps: false,
            saleComps: false,
            favorites: false,
            zoning: false,
            landUse: false,
            iosZoning: false,
            parcels: false,
            counties: false,
            aadt: false,
            opportunityZones: false,
            rails: false,
            pointsOfInterest: false,
        });
    }

    /**
     * Ensures backward compatibility of saved visibleLayers data between app versions.
     * Adds any missing fields from the current default schema while keeping the existing user values.
     * Prevents MobX from losing reactivity when the visibleLayers structure changes after an update.
    */
    private migrateVisibleLayers(saved?: IVisibleLayers | null): IVisibleLayers {
        const migratedLayers = getDefaultVisibleLayers();

        if (saved) {
            for (const key in migratedLayers) {
                if (key in saved) {
                    migratedLayers[key as keyof IVisibleLayers] = saved[key as keyof IVisibleLayers]!;
                }
            }
        }

        return migratedLayers;
    };

    clear() {
        this.baseMap = getDefaultBaseMap();
        this.visibleLayers = getDefaultVisibleLayers();
        this.viewState = getDefaultViewState();
        this.isStyleLoaded = false;
        this.mapMouseEventCallbackItems = [];
    }
}

interface IMapMouseEventCallbackItem {
    key: string;
    type: MapMouseEventType;
    callback: (feature: GeoJSONFeature | undefined, e: MapMouseEvent) => void;
}

const getDefaultBaseMap = () => BaseMap.Streets;

const getDefaultVisibleLayers = (): IVisibleLayers => ({
    openDeals: true,
    wonDeals: true,
    lostDeals: true,
    leads: false,
    leaseComps: false,
    saleComps: false,
    favorites: false,
    zoning: false,
    landUse: false,
    iosZoning: true,
    parcels: false,
    counties: false,
    aadt: false,
    opportunityZones: false,
    rails: false,
    pointsOfInterest: false,
});

const getDefaultViewState = () => ({
    longitude: -77.0366665,
    latitude: 38.8977014,
    zoom: 11,
});